import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Sparkles } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import WhatsAppFloat from "../components/WhatsAppFloat";
import LeadFormModal from "../components/LeadFormModal";
import { BODY_MODELS, buildWhatsAppLink } from "../constants";

// Decorative botanical SVG (line-art flower)
const Bloom = ({ className = "", color = "var(--c-vino)" }) => (
  <svg
    viewBox="0 0 100 100"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    stroke={color}
    strokeWidth="0.8"
  >
    <circle cx="50" cy="50" r="6" />
    {[...Array(8)].map((_, i) => {
      const a = (i * Math.PI * 2) / 8;
      const x = 50 + Math.cos(a) * 22;
      const y = 50 + Math.sin(a) * 22;
      return (
        <ellipse
          key={i}
          cx={x}
          cy={y}
          rx="14"
          ry="6"
          transform={`rotate(${(i * 360) / 8} ${x} ${y})`}
        />
      );
    })}
  </svg>
);

export default function BodysPage() {
  const [active, setActive] = useState(BODY_MODELS[0].slug);
  const [leadOpen, setLeadOpen] = useState(false);
  const activeModel = BODY_MODELS.find((m) => m.slug === active);

  return (
    <main
      data-testid="bodys-page"
      className="min-h-screen bg-page text-ink"
    >
      <Navbar />

      {/* Hero */}
      <section
        id="top"
        data-testid="bodys-hero"
        className="relative pt-28 sm:pt-32 lg:pt-36 pb-16 sm:pb-20 overflow-hidden"
      >
        <Bloom
          className="absolute -top-20 -right-20 w-[420px] h-[420px] opacity-10 rotate-12 hidden md:block"
          color="var(--c-vino)"
        />
        <Bloom
          className="absolute bottom-10 -left-24 w-[320px] h-[320px] opacity-10 -rotate-12 hidden md:block"
          color="var(--c-gold)"
        />

        <div className="relative max-w-7xl mx-auto px-6 sm:px-10">
          <Link
            to="/"
            data-testid="bodys-back-link"
            className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-vino transition-colors mb-6"
          >
            <ArrowLeft size={16} /> Volver al inicio
          </Link>

          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* Text */}
            <div className="lg:col-span-6 z-10">
              <div className="fade-up flex items-center gap-2 mb-5">
                <span className="inline-flex items-center gap-2 rounded-full border border-vino/30 bg-page/60 backdrop-blur-sm px-4 py-1.5 text-[11px] uppercase tracking-[0.3em] text-vino font-medium">
                  <Sparkles size={12} /> Colección Bodys
                </span>
              </div>

              <h1
                data-testid="bodys-title"
                className="fade-up delay-1 font-heading text-[44px] leading-[0.95] sm:text-6xl lg:text-7xl xl:text-[88px] tracking-tighter text-ink"
              >
                Bodys que <span className="italic text-vino">florecen</span>
                <br className="hidden sm:block" />
                contigo<span className="text-vino">.</span>
              </h1>

              <p className="fade-up delay-2 mt-6 max-w-xl text-base sm:text-lg text-ink-soft leading-relaxed font-body">
                Cada modelo lleva el nombre de una flor. Personaliza el tuyo
                con bordado, nombre o logo.
              </p>

              <div className="fade-up delay-3 mt-7 flex flex-col sm:flex-row gap-3">
                <a
                  href="#modelos"
                  data-testid="bodys-explore-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-vino text-page px-7 py-4 text-[15px] font-medium hover:bg-vino-deep transition-all hover:-translate-y-1 shadow-[0_18px_40px_-15px_rgba(114,47,55,0.55)]"
                >
                  Ver los modelos
                </a>
                <button
                  type="button"
                  onClick={() => setLeadOpen(true)}
                  data-testid="bodys-hero-cta"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-vino/40 text-ink px-7 py-4 text-[15px] font-medium hover:bg-sand transition-colors"
                >
                  Pedir catálogo
                </button>
              </div>

              {/* Stats */}
              <div className="fade-up delay-4 mt-10 grid grid-cols-3 gap-4 max-w-md">
                <div data-testid="stat-models">
                  <div className="font-heading text-3xl text-vino">9</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft mt-1">
                    Modelos
                  </div>
                </div>
                <div data-testid="stat-colors">
                  <div className="font-heading text-3xl text-vino">8</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft mt-1">
                    Colores
                  </div>
                </div>
                <div data-testid="stat-sizes">
                  <div className="font-heading text-3xl text-vino">XS–2XL</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft mt-1">
                    Tallas
                  </div>
                </div>
              </div>
            </div>

            {/* Visual */}
            <div className="lg:col-span-6 relative">
              <div className="fade-up delay-2 relative">
                <div className="img-zoom rounded-[2.5rem] overflow-hidden aspect-[4/5] shadow-[0_50px_120px_-30px_rgba(44,36,36,0.45)]">
                  <img
                    src="/bodys/grupo.jpeg"
                    alt="Mujeres reales vistiendo Bodys Vallitas en vinotinto"
                    className="w-full h-full object-cover"
                    data-testid="bodys-campaign-image"
                  />
                </div>

                {/* Floating tag */}
                <div className="absolute -left-4 sm:-left-10 bottom-10 paper-card rounded-2xl px-5 py-4 max-w-[230px] float-anim">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-vino mb-1">
                    Mujeres reales
                  </div>
                  <div className="font-heading text-xl text-ink leading-tight">
                    Cada cuerpo, una flor.
                  </div>
                </div>

                {/* Floating size badge */}
                <div className="hidden sm:flex absolute -right-4 top-10 paper-card rounded-2xl px-5 py-4 items-center gap-3 float-anim" style={{ animationDelay: "1s" }}>
                  <div className="w-10 h-10 rounded-full bg-vino text-page flex items-center justify-center font-heading text-lg">
                    ✦
                  </div>
                  <div>
                    <div className="text-xs text-ink-soft">Talla</div>
                    <div className="font-medium text-sm text-ink">XS – 2XL</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick model selector pills (desktop) + showcase */}
      <section
        data-testid="bodys-spotlight"
        className="relative py-16 sm:py-24 bg-sand"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="flex flex-wrap items-center gap-3 mb-12">
            <span className="text-[11px] uppercase tracking-[0.25em] text-ink-soft mr-2">
              Vista rápida:
            </span>
            {BODY_MODELS.map((m) => (
              <button
                key={m.slug}
                data-testid={`spotlight-pill-${m.slug}`}
                onClick={() => setActive(m.slug)}
                className={`rounded-full px-5 py-2 text-sm font-medium transition-all border ${
                  active === m.slug
                    ? "bg-vino text-page border-vino"
                    : "bg-transparent text-ink border-vino/30 hover:bg-page"
                }`}
              >
                {m.name}
              </button>
            ))}
          </div>

          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 order-2 lg:order-1">
              <div className="text-[11px] uppercase tracking-[0.3em] text-vino font-medium mb-3">
                Modelo destacado
              </div>
              <h2
                key={activeModel.slug}
                data-testid="spotlight-title"
                className="font-heading text-5xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.95] text-ink fade-up"
              >
                {activeModel.name}
              </h2>
              <p className="mt-4 font-heading italic text-2xl sm:text-3xl text-vino">
                {activeModel.tagline}
              </p>
              <p className="mt-6 max-w-lg text-base text-ink-soft leading-relaxed">
                {activeModel.description}
              </p>

              <ul className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-xl">
                {activeModel.features.map((f) => (
                  <li
                    key={f}
                    className="text-[11px] uppercase tracking-[0.18em] text-ink-soft border border-line rounded-full px-3 py-2 bg-page text-center"
                  >
                    {f}
                  </li>
                ))}
              </ul>

              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <Link
                  to={`/bodys/${activeModel.slug}`}
                  data-testid="spotlight-detail-link"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-vino text-page px-7 py-4 text-[15px] font-medium hover:bg-vino-deep transition-all hover:-translate-y-1 shadow-[0_18px_40px_-15px_rgba(114,47,55,0.55)]"
                >
                  Ver detalle del {activeModel.name}
                  <ArrowUpRight size={18} />
                </Link>
                <a
                  href={buildWhatsAppLink(`Hola, me interesa el body modelo ${activeModel.name}. ¿Me puedes dar más información?`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="spotlight-cta"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-vino/40 text-ink px-7 py-4 text-[15px] font-medium hover:bg-page transition-colors"
                >
                  Cotizar por WhatsApp
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 order-1 lg:order-2 relative">
              <div
                key={activeModel.slug + "-img"}
                className="img-zoom rounded-[2rem] overflow-hidden aspect-[4/5] shadow-[0_50px_120px_-30px_rgba(44,36,36,0.45)] fade-up"
              >
                <img
                  src={activeModel.image}
                  alt={`Body Vallitas modelo ${activeModel.name}`}
                  className="w-full h-full object-cover"
                  data-testid="spotlight-image"
                />
              </div>
              <Bloom
                className="absolute -bottom-8 -right-6 w-32 h-32 opacity-25 rotate-12"
                color="var(--c-gold)"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Editorial gallery — alternating asymmetric layout */}
      <section
        id="modelos"
        data-testid="bodys-gallery"
        className="relative py-24 sm:py-32"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-16 sm:mb-24">
            <div className="max-w-2xl">
              <div className="label-eyebrow mb-4">El jardín completo</div>
              <h2
                data-testid="gallery-title"
                className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink leading-[1.05]"
              >
                Top cinco modelos,
                <br />
                <span className="italic text-vino">una sola promesa</span>:
                quedarte como un guante.
              </h2>
            </div>
            <p className="max-w-md text-base text-ink-soft">
              Todos disponibles en vinotinto, además de colores por encargo.
              Talla S a XL · personalización con nombre, frase o logo.
            </p>
          </div>

          <div className="space-y-24 sm:space-y-32">
            {BODY_MODELS.filter((m) => !m.hideFromGallery).map((m, idx) => {
              const reverse = idx % 2 === 1;
              return (
                <article
                  key={m.slug}
                  data-testid={`model-card-${m.slug}`}
                  id={m.slug}
                  className={`grid lg:grid-cols-12 gap-8 lg:gap-16 items-center ${
                    reverse ? "lg:[direction:rtl]" : ""
                  }`}
                >
                  <div className="lg:col-span-7 relative lg:[direction:ltr]">
                    <Link
                      to={`/bodys/${m.slug}`}
                      data-testid={`model-image-link-${m.slug}`}
                      className="block img-zoom rounded-[2rem] overflow-hidden aspect-[4/5] sm:aspect-[5/6]"
                    >
                      <img
                        src={m.image}
                        alt={`Body Vallitas modelo ${m.name}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </Link>
                    <span
                      className={`absolute font-heading italic text-[20vw] sm:text-[140px] lg:text-[180px] leading-none text-cream -z-0 select-none pointer-events-none ${
                        reverse
                          ? "-right-2 sm:-right-6 -bottom-6"
                          : "-left-2 sm:-left-6 -bottom-6"
                      }`}
                      aria-hidden="true"
                    >
                      0{idx + 1}
                    </span>
                  </div>

                  <div className="lg:col-span-5 lg:[direction:ltr]">
                    <div className="text-[11px] uppercase tracking-[0.3em] text-vino font-medium mb-3">
                      Modelo {String(idx + 1).padStart(2, "0")}
                    </div>
                    <h3 className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-ink">
                      {m.name}
                    </h3>
                    <p className="mt-3 font-heading italic text-xl sm:text-2xl text-vino">
                      {m.tagline}
                    </p>
                    <p className="mt-5 text-[15px] sm:text-base text-ink-soft leading-relaxed">
                      {m.description}
                    </p>

                    <ul className="mt-6 flex flex-wrap gap-2">
                      {m.features.map((f) => (
                        <li
                          key={f}
                          className="text-[11px] uppercase tracking-[0.18em] text-ink-soft border border-line rounded-full px-3 py-1.5 bg-page"
                        >
                          {f}
                        </li>
                      ))}
                    </ul>

                    <div className="mt-8 flex flex-col sm:flex-row gap-3">
                      <Link
                        to={`/bodys/${m.slug}`}
                        data-testid={`model-detail-${m.slug}`}
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-vino text-page px-6 py-3 text-sm font-medium hover:bg-vino-deep transition-all hover:-translate-y-0.5"
                      >
                        Ver detalle del {m.name}
                        <ArrowUpRight size={16} />
                      </Link>
                      <a
                        href={buildWhatsAppLink(`Hola Vallitas, me interesa el body modelo ${m.name}.`)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`model-cta-${m.slug}`}
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-vino/40 text-ink px-6 py-3 text-sm font-medium hover:bg-sand transition-colors"
                      >
                        Cotizar
                      </a>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Personalization callout */}
      <section
        data-testid="bodys-customize"
        className="relative py-24 sm:py-32 bg-vino text-page overflow-hidden"
      >
        <Bloom
          className="absolute -top-12 -left-12 w-72 h-72 opacity-15"
          color="var(--c-page)"
        />
        <Bloom
          className="absolute -bottom-12 -right-12 w-80 h-80 opacity-15"
          color="var(--c-page)"
        />

        <div className="relative max-w-4xl mx-auto px-6 sm:px-10 text-center">
          <div className="text-[11px] uppercase tracking-[0.35em] text-cream font-medium mb-6">
            Tu body, tu firma
          </div>
          <h2 className="font-heading text-4xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
            Hazlo <em className="text-[#EADCCF] not-italic">único</em> con tu nombre,
            logo o frase.
          </h2>
          <p className="mt-8 max-w-2xl mx-auto text-base sm:text-lg text-cream/90 leading-relaxed">
            Bordado, sublimación o estampado de alta calidad. Cuéntanos qué
            tienes en mente y diseñamos contigo en minutos.
          </p>
          <a
            href={buildWhatsAppLink("Hola, quiero personalizar un body Vallitas.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="customize-cta"
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-page text-ink px-8 py-4 text-[15px] font-medium hover:bg-cream transition-all hover:-translate-y-1"
          >
            Personalizar mi body
            <ArrowUpRight size={18} />
          </a>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
      <LeadFormModal
        open={leadOpen}
        onClose={() => setLeadOpen(false)}
        source="catalog_request"
      />
    </main>
  );
}
